// Design tokens — the SINGLE SOURCE OF TRUTH (REQ-DS-001/003). This TS module generates
// `src/design/tokens.css` (a test asserts they match; regenerate with `npm run gen:tokens`).
// Components reference the semantic custom properties only — never a raw hex/px.
//
// The scale follows Ant Design (spacing / radii / control-height / type / motion), mapped onto
// our colours (REQ-DS-005). Commit 2 ships the current (dark) palette as tokens; commit 3 adds
// the light theme + a manual toggle. Colour token NAMES are stable across that change.

export type TokenGroup = { title: string; entries: Array<[string, string]> };

// ── Colour (semantic) — two themes, same token NAMES (REQ-DS-002) ─────────────
// Dark = the original palette (unchanged from commit 2). Light = an AA-gated companion.
// A token's lightness may flip between themes (e.g. --color-private is a light violet on
// dark, a dark violet on light) so a component reads correctly in both.
// AA-verified values (component-a11y-spec §1). Every text token ≥4.5:1 on its background in both
// themes; non-text/focus/edges ≥3:1. There is no `--faint` text token (retired, A11Y-1).
export const DARK: Record<string, string> = {
  "color-bg": "#10151f",
  "color-surface": "#19202e",
  "color-surface-sunken": "#1f2735", // = spec surface-2
  "color-text": "#eef1f7",
  "color-text-muted": "#a8b1c6", // ≈6:1 on surface — the single meta-text grey
  "color-divider": "#232c3c", // hairline row/section dividers (non-text)
  "color-border": "#2b3446", // panel/control borders (non-text)
  "color-border-strong": "#7d8aa1", // control/input edges — ≥3:1 vs the field (WCAG 1.4.11)
  "color-focus": "#8ea2ff", // primary-based ring, ≥3:1 on bg/surface
  "color-accent": "#8ea2ff",
  "color-accent-contrast": "#0c1220",
  "color-danger": "#ff6b66",
  "color-warning": "#e6b552",
  "color-warning-soft": "#2a2313", // Slipped pill bg (dark)
  "color-success": "#5ec88b",
  "color-info": "#7dd3fc",
  "color-event": "#7dd3fc",
  "color-task": "#a5b4fc",
  "color-note": "#fcd34d",
  "color-grocery": "#6ee7b7",
  "color-private": "#c4b5fd",
  "color-on-owner": "#ffffff", // initial on an owner avatar mark (decorative; a11y via initial+aria-label)
  "color-owner-1": "#6ba6ff",
  "color-owner-2": "#3fd3c6",
  "color-owner-3": "#f386bb",
  "color-owner-4": "#f0b56b",
  "color-owner-5": "#c084fc",
  "color-owner-6": "#8bd45e",
};

export const LIGHT: Record<string, string> = {
  "color-bg": "#eef1f6",
  "color-surface": "#ffffff",
  "color-surface-sunken": "#f4f6fa", // = spec surface-2 (segmented track, hover, inputs)
  "color-text": "#1b2330", // ≈14:1 on surface
  "color-text-muted": "#545b6e", // ≈4.9:1 on surface — the only grey allowed for meta text
  "color-divider": "#e9ecf3", // hairline row/section dividers (non-text)
  "color-border": "#dfe3ec", // panel/control borders (non-text)
  "color-border-strong": "#64748b", // control edges — ≥3:1 vs the light field
  "color-focus": "#3a55c9", // primary-based ring
  "color-accent": "#3a55c9", // ≈5.3:1 on white
  "color-accent-contrast": "#ffffff",
  "color-danger": "#c23b34",
  "color-warning": "#8a5600", // warn-text; ≈5:1 on the warn-soft tint
  "color-warning-soft": "#fbf3e2", // Slipped pill bg
  "color-success": "#1f7a4d",
  "color-info": "#3a55c9",
  "color-event": "#0369a1",
  "color-task": "#4f46e5",
  "color-note": "#8a5600",
  "color-grocery": "#1f7a4d",
  "color-private": "#6d28d9",
  "color-on-owner": "#ffffff",
  // Family owner marks (mockup-v9): david · rinni · flo · amelia, then two extras. ≥3:1 + distinct.
  "color-owner-1": "#3567c0",
  "color-owner-2": "#0f9a8f",
  "color-owner-3": "#cf4e8b",
  "color-owner-4": "#c47a1e",
  "color-owner-5": "#7c3aed",
  "color-owner-6": "#4a7a2f",
};

export const THEMES: Record<"light" | "dark", Record<string, string>> = { light: LIGHT, dark: DARK };
/** Default colour map for helpers/tests that don't specify a theme (the dark original). */
export const COLORS = DARK;

// ── Non-colour scales (Ant Design) ───────────────────────────────────────────
const SPACE: TokenGroup = {
  title: "space (4-based)",
  entries: [
    ["space-1", "4px"], ["space-2", "8px"], ["space-3", "12px"], ["space-4", "16px"],
    ["space-5", "20px"], ["space-6", "24px"], ["space-8", "32px"], ["space-10", "40px"],
  ],
};
const RADIUS: TokenGroup = {
  title: "radius",
  entries: [["radius-sm", "8px"], ["radius-md", "12px"], ["radius-lg", "16px"], ["radius-pill", "999px"]],
};
const TYPE: TokenGroup = {
  title: "type",
  entries: [
    ["font-size-xs", "12px"], ["font-size-meta", "13px"], ["font-size-sm", "14px"],
    ["font-size-time", "15px"], ["font-size-base", "16px"],
    ["font-size-md", "18px"], ["font-size-lg", "20px"], ["font-size-xl", "24px"],
    ["font-size-date", "26px"], ["font-size-2xl", "30px"],
    ["line-tight", "1.25"], ["line-base", "1.5"],
    ["weight-regular", "400"], ["weight-medium", "600"], ["weight-bold", "700"], ["weight-black", "800"],
    // Inter, self-hosted (public/fonts/inter-*.woff2) — the mock's face; system stack falls back.
    ["font-family", "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"],
  ],
};
const CONTROL: TokenGroup = {
  title: "controls & targets",
  entries: [["touch-target-min", "44px"], ["control-height", "44px"]],
};
const MOTION: TokenGroup = {
  title: "motion",
  entries: [["motion-fast", "120ms"], ["motion-base", "200ms"], ["ease-standard", "cubic-bezier(0.2, 0, 0, 1)"]],
};
const ELEVATION: TokenGroup = {
  title: "elevation (shadow, paired with a border — never the sole edge)",
  entries: [
    ["elevation-0", "none"],
    ["elevation-1", "0 1px 2px rgba(0, 0, 0, 0.3)"],
    ["elevation-2", "0 6px 16px rgba(0, 0, 0, 0.35)"],
    ["elevation-3", "0 12px 32px rgba(0, 0, 0, 0.45)"],
  ],
};
const LAYOUT: TokenGroup = {
  title: "layout",
  entries: [["content-max", "34rem"], ["shell-max", "1100px"], ["rail-width", "216px"], ["bg-image", "none"], ["z-sheet", "100"], ["z-toast", "200"]],
};
// mockup-v9 chrome shadows (sheet/FAB/toast/panel). Kept as tokens so style.css carries no raw
// colour/px — the FAB glow references the live accent via color-mix.
const SHADOW: TokenGroup = {
  title: "chrome shadows (mockup-v9)",
  entries: [
    ["shadow-sheet", "0 -6px 30px rgba(16, 24, 40, 0.16)"],
    ["shadow-fab", "0 8px 22px color-mix(in srgb, var(--color-accent) 38%, transparent)"],
    ["shadow-toast", "0 8px 24px rgba(16, 24, 40, 0.2)"],
    ["shadow-panel", "0 10px 40px rgba(16, 24, 40, 0.14)"],
    ["shadow-seg", "0 1px 2px rgba(0, 0, 0, 0.08)"],
  ],
};

/** Theme-agnostic scales (colour lives in the per-theme blocks). */
export const SCALE_GROUPS: TokenGroup[] = [SPACE, RADIUS, TYPE, CONTROL, MOTION, ELEVATION, SHADOW, LAYOUT];

const colorVars = (colors: Record<string, string>, indent: string): string =>
  Object.entries(colors)
    .map(([k, v]) => `${indent}--${k}: ${v};`)
    .join("\n");

/** Deterministic generator for `src/design/tokens.css`. Light is the base; dark applies via
 *  `prefers-color-scheme`; a `[data-theme]` attribute (the persisted manual toggle) overrides
 *  both — matching the theme-toggle contract. */
export function generateCss(): string {
  const lines: string[] = [
    "/* GENERATED from src/design/tokens.ts — do not edit by hand. Run `npm run gen:tokens`. */",
    ":root {",
  ];
  for (const g of SCALE_GROUPS) {
    lines.push(`  /* ${g.title} */`);
    for (const [name, value] of g.entries) lines.push(`  --${name}: ${value};`);
  }
  lines.push("  /* colour (semantic) — light is the base theme */");
  lines.push(colorVars(LIGHT, "  "));
  lines.push("}");
  lines.push("", "@media (prefers-color-scheme: dark) {", "  :root {", colorVars(DARK, "    "), "  }", "}");
  lines.push("", ':root[data-theme="dark"] {', colorVars(DARK, "  "), "}");
  lines.push("", ':root[data-theme="light"] {', colorVars(LIGHT, "  "), "}");
  lines.push("");
  return lines.join("\n");
}

// ── Contrast pairs the gate enforces (REQ-DS-002; imported typed, not regex-scraped) ─────────
export type ContrastPair = { fg: string; bg: string; min: number; kind: "text" | "non-text"; label: string };

export function contrastPairs(colors: Record<string, string> = COLORS): ContrastPair[] {
  const g = (n: string) => colors[n];
  return [
    // information-bearing text ≥ 4.5:1
    { fg: g("color-text"), bg: g("color-bg"), min: 4.5, kind: "text", label: "text on bg" },
    { fg: g("color-text"), bg: g("color-surface"), min: 4.5, kind: "text", label: "text on surface" },
    { fg: g("color-text"), bg: g("color-surface-sunken"), min: 4.5, kind: "text", label: "text on sunken" },
    { fg: g("color-text-muted"), bg: g("color-surface"), min: 4.5, kind: "text", label: "muted on surface" },
    { fg: g("color-text-muted"), bg: g("color-bg"), min: 4.5, kind: "text", label: "muted on bg" },
    { fg: g("color-text-muted"), bg: g("color-surface-sunken"), min: 4.5, kind: "text", label: "muted on sunken" },
    { fg: g("color-accent-contrast"), bg: g("color-accent"), min: 4.5, kind: "text", label: "button label on accent" },
    { fg: g("color-danger"), bg: g("color-surface"), min: 4.5, kind: "text", label: "danger text on surface" },
    { fg: g("color-danger"), bg: g("color-surface-sunken"), min: 4.5, kind: "text", label: "danger text on sunken" },
    // non-text UI ≥ 3:1
    { fg: g("color-focus"), bg: g("color-bg"), min: 3, kind: "non-text", label: "focus ring on bg" },
    { fg: g("color-focus"), bg: g("color-surface"), min: 3, kind: "non-text", label: "focus ring on surface" },
    { fg: g("color-border-strong"), bg: g("color-surface-sunken"), min: 3, kind: "non-text", label: "control edge on field" },
  ];
}

/** Owner swatches: decorative, ≥3:1 non-text on surface + mutually distinct. */
export function ownerColors(colors: Record<string, string> = COLORS): string[] {
  return [1, 2, 3, 4, 5, 6].map((n) => colors[`color-owner-${n}`]);
}
